import { ref } from 'vue';
import type { LocationQueryRaw } from 'vue-router';

import { filesTableSearchSchema as schema, type FormValues, type FileTableSortColumn } from './schema';

import { useQueryFilter } from '@/ui/Table/query/useQueryFilter';
import type { TableSearchParameters } from '@/ui/Table/tableProperties';

const DEFAULT_FIRST = 20;

interface UseQueryFilterFilesOptions {
  syncUrl?: boolean;
}

export const useQueryFilterFiles = ({ syncUrl = true }: UseQueryFilterFilesOptions = {}) => {
  const router = useRouter();
  const {
    filters,
    setUrl,
    onPage,
    onSorting,
    orderBy,
  } = useQueryFilter<FileTableSortColumn, typeof schema>(schema, syncUrl);

  const state = ref<FormValues>({
    id: filters.value.id,
    idOperator: filters.value.idOperator ?? 'in',
    name: filters.value.name,
    nameOperator: filters.value.nameOperator ?? 'contains',
    originalName: filters.value.originalName,
    originalNameOperator: filters.value.originalNameOperator ?? 'contains',
    mimeType: filters.value.mimeType,
    mimeTypeOperator: filters.value.mimeTypeOperator ?? 'contains',
    ext: filters.value.ext,
    extOperator: filters.value.extOperator ?? 'contains',
    path: filters.value.path,
    pathOperator: filters.value.pathOperator ?? 'contains',
    uri: filters.value.uri,
    uriOperator: filters.value.uriOperator ?? 'contains',
    size: filters.value.size,
    sizeOperator: filters.value.sizeOperator ?? '<=',
    creationTime: filters.value.creationTime,
    creationTimeOperator: filters.value.creationTimeOperator ?? '<=',
    lastUpdateTime: filters.value.lastUpdateTime,
    lastUpdateTimeOperator: filters.value.lastUpdateTimeOperator ?? '<=',
    createdBy: filters.value.createdBy,
    createdByOperator: filters.value.createdByOperator ?? 'in',
    lastUpdateBy: filters.value.lastUpdateBy,
    lastUpdateByOperator: filters.value.lastUpdateByOperator ?? 'in',
    lastUpdateByType: filters.value.lastUpdateByType,
    lastUpdateByTypeOperator: filters.value.lastUpdateByTypeOperator ?? 'contains',
    lastUpdateReason: filters.value.lastUpdateReason,
    lastUpdateReasonOperator: filters.value.lastUpdateReasonOperator ?? 'contains',
    first: filters.value.first,
  });

  watchDebounced(
    state,
    (newState) => {
      setUrl(newState);
    },
    { debounce: 400, maxWait: 800, deep: true },
  );

  const setTableQuery = (values: TableSearchParameters) => {
    const orderEntry = values.orderBy?.[0];
    const merged = {
      ...filters.value,
      page: values.page && values.page > 1 ? values.page : undefined,
      first: values.first && values.first !== DEFAULT_FIRST ? values.first : undefined,
      column: orderEntry?.column as FileTableSortColumn | undefined,
      order: orderEntry?.order,
    };
    const valid = schema.validateSync(merged, {
      stripUnknown: true,
      context: { isForURL: true },
    }) as LocationQueryRaw;
    if (syncUrl) {
      router.replace({ query: valid });
    }
  };

  return {
    filters,
    orderBy,
    onPage,
    onSorting,
    state,
    setTableQuery,
  };
};
